import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../../api';
import * as logger from '../../../logger';
import * as rootReducer from '../../../reducers/root';
import * as store from '../../../reducers/store';

import { Button } from '../../../components/Button';
import { InfoMessage } from '../../../components/InfoMessage';

import pageStyles from '../../FormStart/index.module.css';
import styles from './index.module.css';
import defaultStyles from '../../../styles/formSetStyles.module.css';

interface Props extends StateProps, DispatchProps, WithTranslation {
  navigateToNextPage: () => void;
}

interface StateProps {
  formSetId: string;
  patientFeedback: store.PatientFeedback | null;
}

interface DispatchProps {
  setPatientFeedback: (patientFeedback: store.PatientFeedback) => void;
}

interface State {
  selectedFeedback?: api.PatientFeedback.FeedbackType;
  comment?: string;
  errorMsg?: string;
  submitting: boolean;
}

export class FeedbackComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      submitting: false,
    };
  }

  componentDidMount() {
    const { patientFeedback } = this.props;
    if (patientFeedback) {
      this.setState({
        selectedFeedback: patientFeedback.feedback,
        comment: patientFeedback.comment || undefined,
      });
    }
  }

  selectFeedback(feedback: api.PatientFeedback.FeedbackType) {
    this.setState({ selectedFeedback: feedback });
  }

  getButtonStyle(feedback: api.PatientFeedback.FeedbackType): string {
    const { selectedFeedback } = this.state;
    if (!selectedFeedback) return '';
    return selectedFeedback === feedback ? styles.Selected : styles.Unselected;
  }

  handleSubmitFeedback = async () => {
    if (this.state.submitting) {
      return;
    }

    this.setState({ errorMsg: undefined, submitting: true });

    try {
      // In the scheduling flow, reloading the feedback page will kill the formSetId prop but
      // formSetId should still be in localStorage
      const formSetId =
        this.props.formSetId ?? localStorage.getItem('formSetId');
      const { selectedFeedback, comment } = this.state;
      if (!selectedFeedback) {
        return;
      }
      const feedback = await api.client.PatientFeedback.upsert({
        previsitFormSetId: formSetId,
        feedback: selectedFeedback,
        comment,
      });
      this.props.setPatientFeedback(feedback);
      await this.props.navigateToNextPage();
    } catch (e) {
      this.setState({
        errorMsg: this.props.t('There was an error. Please try again.'),
        submitting: false,
      });
      logger.error(e, `[FeedbackComponent.handleSubmitFeedback] error`);
    }
  };

  render() {
    const feedbackType = api.PatientFeedback.FeedbackType;
    return (
      <div className={styles.FeedbackContainer}>
        <div className={pageStyles.PageWrapper}>
          {this.state.errorMsg ? (
            <InfoMessage isError={true}>{this.state.errorMsg}</InfoMessage>
          ) : null}
          <div className={defaultStyles.Label}>
            {this.props.t('Was this experience easy to use?')}
          </div>
          <div className={styles.Feedbacks}>
            <Button
              className={`${styles.Thumb} ${styles.Down} ${this.getButtonStyle(
                feedbackType.Negative,
              )}`}
              onClick={() => this.selectFeedback(feedbackType.Negative)}
              aria-label={this.props.t('Negative Feedback')}
            />
            <Button
              className={`${styles.Thumb} ${this.getButtonStyle(
                feedbackType.Positive,
              )}`}
              onClick={() => this.selectFeedback(feedbackType.Positive)}
              aria-label={this.props.t('Positive Feedback')}
            />
          </div>
          {this.state.selectedFeedback !== undefined && (
            <>
              <label
                className={[defaultStyles.Label, styles.HasHelptext].join(' ')}
                htmlFor="feedback-comments"
              >
                {this.state.selectedFeedback === feedbackType.Negative
                  ? this.props.t('What made the experience challenging?')
                  : this.props.t('What made the experience easy to use?')}
              </label>
              <p className={defaultStyles.HelpText}>
                {this.props.t(
                  'Please don’t share any sensitive personal information, as this feedback will be shared with non-clinical members of our team to improve the experience.',
                )}
              </p>
              <textarea
                id="feedback-comments"
                placeholder={
                  this.state.selectedFeedback === feedbackType.Negative
                    ? this.props.t('Please explain (optional)')
                    : this.props.t('Tell us what you loved! (optional)')
                }
                className={[defaultStyles.Textarea, styles.DetailsInput].join(
                  ' ',
                )}
                maxLength={1000}
                value={this.state.comment}
                onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
                  this.setState({ comment: event.currentTarget.value })
                }
              />
            </>
          )}
          <div className={styles.ButtonSubmitWrapper}>
            <Button
              type="button"
              disabled={!this.state.selectedFeedback || this.state.submitting}
              children={this.props.t('Continue')}
              onClick={this.handleSubmitFeedback}
            />
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps({
  patientFeedback,
  formSets,
}: store.Loaded): StateProps {
  return {
    patientFeedback,
    formSetId: Object.values(formSets)?.[0]?.id,
  };
}

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setPatientFeedback: (patientFeedback: store.PatientFeedback) =>
      dispatch(rootReducer.setPatientFeedback(patientFeedback)),
  };
}

export const FeedbackPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(FeedbackComponent));
